/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-shadow */
/* eslint-disable react/no-unknown-property */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { useFrame, useThree, extend } from '@react-three/fiber'
import { Box3, DirectionalLight, MathUtils, Sphere, Vector3 } from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'

import { bool, number, node } from 'prop-types'

extend({ OrbitControls })

function Stage({ children, intensity = 1, adjustCamera = true, ...props }) {
  const { camera, gl, scene } = useThree()

  const outer = useRef(null)
  const inner = useRef(null)
  const [{ radius, width, height }, set] = useState({ radius: 0, width: 0, height: 0 })

  const orbit = useRef()
  const timeoutRef = useRef(null)
  const [autoRotate, setAutoRotate] = useState(true)

  useLayoutEffect(() => {
    outer.current.position.set(0, 0, 0)
    outer.current.updateWorldMatrix(true, true)
    const box3 = new Box3().setFromObject(inner.current)
    const center = new Vector3()
    const sphere = new Sphere()
    const height = box3.max.y - box3.min.y
    const width = box3.max.x - box3.min.x
    box3.getCenter(center)
    box3.getBoundingSphere(sphere)
    set({
      radius: sphere.radius,
      width,
      height,
    })
    outer.current.position.set(-center.x, -center.y + height / 2, -center.z)
  }, [children])

  useLayoutEffect(() => {
    if (adjustCamera && width && height && radius) {
      const fov = MathUtils.degToRad(camera.fov)
      const diagonal = Math.sqrt(height ** 2 + width ** 2)
      const padding = 1

      const distance = ((diagonal / 2) * padding) / Math.tan(fov / 2)

      const yOffset = height / 2
      camera.position.set(0, yOffset + 3.5, distance)
      camera.lookAt(0, yOffset, 0)
      camera.near = 0.1
      camera.far = Math.max(5000, distance * 2)
      camera.updateProjectionMatrix()

      if (orbit.current) {
        orbit.current.target.set(0, yOffset, 0)
        orbit.current.update()
      }
    }
  }, [radius, height, width, adjustCamera, camera])

  useLayoutEffect(() => {
    if (inner.current) {
      const y = radius / (height > width ? 1.5 : 2) + 0

      const dirLight1 = new DirectionalLight(0x0c4f83, intensity * 1)
      dirLight1.position.set(-8.9, 8.3, 1.67)
      dirLight1.target.position.set(0, y, 0)
      scene.add(dirLight1.target)
      scene.add(dirLight1)

      const dirLight2 = new DirectionalLight(0xffffff, intensity * 2.3)
      dirLight2.position.set(0, 12, 0)
      scene.add(dirLight2)

      const dirLight3 = new DirectionalLight(0xffffff, intensity * 1.3)
      dirLight3.position.set(-1.67, 9.3, 9.7)
      scene.add(dirLight3)

      const dirLight4 = new DirectionalLight(0x1e6c0f, intensity * 0.5)
      dirLight4.position.set(10, 10, -3)
      scene.add(dirLight4)

      return () => {
        scene.remove(dirLight1.target)
        scene.remove(dirLight1)

        scene.remove(dirLight2)
        scene.remove(dirLight3)
        scene.remove(dirLight4)
      }
    }
  }, [radius, width, height, intensity, scene])

  useEffect(() => {
    const controls = orbit.current
    if (!controls) return

    const handleStart = () => {
      if (controls.autoRotate) {
        setAutoRotate(false)
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }

    const handleEnd = () => {
      timeoutRef.current = setTimeout(() => {
        setAutoRotate(true)
      }, 1800)
    }

    controls.addEventListener('start', handleStart)
    controls.addEventListener('end', handleEnd)

    return () => {
      controls.removeEventListener('start', handleStart)
      controls.removeEventListener('end', handleEnd)
      clearTimeout(timeoutRef.current)
    }
  }, [])

  useFrame(() => {
    if (orbit.current) {
      orbit.current.update()
    }
  })

  return (
    <>
      <group {...props}>
        <group ref={outer}>
          <group ref={inner}>{children}</group>
        </group>
      </group>
      <ambientLight intensity={intensity * 0.3} color={0xffffff} />
      <orbitControls
        ref={orbit}
        args={[camera, gl.domElement]}
        enableDamping
        enableZoom={false}
        enablePan={false}
        enableRotate
        autoRotate={autoRotate}
        autoRotateSpeed={3}
        dampingFactor={0.04}
        minDistance={radius * 1.2}
        maxDistance={radius * 3}
        minPolarAngle={Math.PI / 6}
        maxPolarAngle={Math.PI / 2}
      />
    </>
  )
}

Stage.propTypes = {
  children: node.isRequired,
  intensity: number,
  adjustCamera: bool,
}

export { Stage }
